import { Box, Divider, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { FormattedMessage } from 'react-intl';
import { DicomRoi } from '~/domain/dicom/dicomRoi';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { StructureSet } from '~/domain/patient/structureset';
import DoseConfirmation from './DoseConfirmation';
import StructureSetConfirmation from './StructureSetConfirmation';

type ExportConfirmationProps = {
    open: boolean;
    setOpen: (open: boolean) => void;
    onConfirm: () => void;
    selectedStructureSets: StructureSet[];
    selectedDoses: DoseMatrix[];
    selectedRegistrations: RigidRegistration[];
    structureSelections: Record<string, DicomRoi[]>;
};

export default function ExportConfirmation({
    open,
    setOpen,
    onConfirm,
    selectedStructureSets,
    selectedDoses,
    selectedRegistrations,
    structureSelections,
}: ExportConfirmationProps) {
    const handleClose = () => {
        setOpen(false);
    };

    const hasDose = selectedDoses.length > 0;
    const hasStructureSet = selectedStructureSets.length > 0;
    const hasRegistration = selectedRegistrations.length > 0;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            PaperProps={{
                elevation: 0,
                'data-testid': 'export-confirmation-dialog',
            }}
            fullWidth
        >
            <DialogTitle>
                <FormattedMessage id="confirmDICOMExport" />
            </DialogTitle>
            <DialogContent sx={{ display: 'grid', gap: 2, pb: 1 }}>
                <Typography fontSize={14}>
                    You are about to export the selected datasets through DICOM.
                    Are you sure you want to proceed?
                </Typography>
                {hasStructureSet && (
                    <StructureSetConfirmation
                        selectedStructureSets={selectedStructureSets}
                        structureSelections={structureSelections}
                    />
                )}
                {hasDose && (
                    <>
                        {hasStructureSet && (
                            <Divider flexItem orientation="horizontal" />
                        )}
                        <DoseConfirmation selectedDoses={selectedDoses} />
                    </>
                )}
                {hasRegistration && (
                    <>
                        {(hasStructureSet || hasDose) && (
                            <Divider flexItem orientation="horizontal" />
                        )}
                        <Typography variant="subtitle2">
                            Registrations ({selectedRegistrations.length})
                        </Typography>
                        {selectedRegistrations.map((reg) => (
                            <Typography
                                key={reg.dicomUid}
                                variant="body2"
                                color="text.secondary"
                            >
                                {reg.contentLabel ||
                                    reg.contentDescription ||
                                    reg.dicomUid}
                            </Typography>
                        ))}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Box display="flex" gap={1}>
                    <Button
                        onClick={handleClose}
                        data-testid="export-confirmation-cancel-button"
                    >
                        <FormattedMessage id="cancel" />
                    </Button>
                    <Button
                        onClick={onConfirm}
                        autoFocus
                        data-testid="export-confirmation-confirm-button"
                    >
                        <FormattedMessage id="confirm" />
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}
